import React, {useEffect, useRef, useState} from 'react';
import queryString from 'query-string';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCheck, faEnvelope} from '@fortawesome/free-solid-svg-icons';
import {Button, Col, Container, Row} from '@themesberg/react-bootstrap';
import {Link} from 'react-router-dom';
import {FormattedMessage} from 'react-intl';

import {Routes} from '../../routes';
import API from '../../services/API';
import Utils from '../../services/Utils';
import BrandLockup from '../../components/BrandLockup';
import {ACCESS_TOKEN} from '../../services/constants';

// Fonctionnalités débloquées, listées au chauffeur qui vient de passer au payant depuis son dashboard.
const PAID_FEATURES = ['FEAT_BOOKINGS_UNLIMITED', 'FEAT_VEHICLES_UNLIMITED', 'FEAT_MODULE_UNLIMITED', 'FEAT_ONLINE_PAYMENT', 'FEAT_CALENDAR_SYNC'];

// Premiers pas d'un nouvel abonné : se connecter, configurer, intégrer.
const ONBOARDING_STEPS = ['SIGNIN', 'CONFIGURE', 'EMBED'];

// Le compte est créé par le webhook Stripe, qui peut arriver après la redirection : on interroge
// l'état de la session par courtes passes plutôt que d'afficher tout de suite un écran incomplet.
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 15; // ~30 s, au-delà on bascule sur l'écran « activation en cours »

export default (props) => {
  Utils.setBackground(true);
  const sessionId = queryString.parse(props.location.search).session_id;
  // Sans session_id lisible (retour direct, session expirée côté Stripe), on se rabat sur le jeton
  // local pour savoir s'il s'agit d'un compte déjà connecté ou d'une inscription.
  const loggedIn = !!localStorage.getItem(ACCESS_TOKEN);

  const [status, setStatus] = useState(null);
  const [pending, setPending] = useState(!!sessionId); // true tant que le webhook n'a pas provisionné
  const timer = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      return undefined;
    }
    let attempts = 0;
    let stopped = false;

    const poll = () => {
      API.getSubscriptionSession(sessionId).then(response => {
        if (stopped) return;
        const data = response && response.data && response.data.success === true ? response.data.data : null;
        if (data) {
          setStatus(data);
        }
        attempts += 1;
        if (data && data.ready) {
          setPending(false);
        } else if (attempts >= POLL_MAX_ATTEMPTS) {
          setPending(false); // on n'attend pas indéfiniment : écran « activation en cours »
        } else {
          timer.current = setTimeout(poll, POLL_INTERVAL_MS);
        }
      });
    };
    poll();

    return () => {
      stopped = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [sessionId]);

  const ready = !!(status && status.ready);
  // Montée en gamme d'un compte existant : pas d'email d'identifiants, le chauffeur retourne au dashboard.
  const upgrade = status ? status.existingAccount : loggedIn;
  // Session illisible (retour direct, session Stripe expirée) : on ne prétend pas connaître la formule.
  const planKey = status && status.interval === 'ANNUAL' ? 'PLAN_ANNUAL'
    : (status && status.interval === 'MONTHLY' ? 'PLAN_MONTHLY' : null);
  const subtitleKey = (upgrade ? 'WELCOME_UPGRADE_SUBTITLE' : 'WELCOME_SUBTITLE') + (planKey ? '' : '_NO_PLAN');

  // ── Attente du webhook ─────────────────────────────────────────────────────
  if (pending) {
    return (
      <Layout>
        <div className="cc-welcome-waiting">
          <i className="spinner-border text-primary"/>
          <h3 className="mt-4 mb-2"><FormattedMessage id="WELCOME_ACTIVATING_TITLE"/></h3>
          <p className="text-muted mb-0"><FormattedMessage id="WELCOME_ACTIVATING_BODY"/></p>
        </div>
      </Layout>
    );
  }

  // ── Paiement encaissé mais compte pas encore actif au bout de ~30 s ────────
  if (sessionId && !ready && status && status.paid) {
    return (
      <Layout>
        <div className="text-center">
          <span className="cc-welcome-badge is-pending"><FontAwesomeIcon icon={faEnvelope}/></span>
          <h2 className="cc-welcome-title"><FormattedMessage id="WELCOME_PENDING_TITLE"/></h2>
          <p className="cc-welcome-lead"><FormattedMessage id="WELCOME_PENDING_BODY"/></p>
          <Button as={Link} to={upgrade ? Routes.DashboardOverview.path : Routes.Signin.path}
                  variant="primary" className="cc-welcome-cta">
            <FormattedMessage id={upgrade ? 'WELCOME_GO_TO_DASHBOARD' : 'SIGN_IN'}/>
          </Button>
          <Help/>
        </div>
      </Layout>
    );
  }

  // ── Session non réglée ou illisible : on n'annonce pas un succès qu'on n'a pas constaté ────
  if (sessionId && !ready) {
    return (
      <Layout>
        <div className="text-center">
          <span className="cc-welcome-badge is-pending"><FontAwesomeIcon icon={faEnvelope}/></span>
          <h2 className="cc-welcome-title"><FormattedMessage id="WELCOME_UNCONFIRMED_TITLE"/></h2>
          <p className="cc-welcome-lead"><FormattedMessage id="WELCOME_UNCONFIRMED_BODY"/></p>
          <div className="cc-welcome-actions">
            <Button as={Link} to={Routes.Signin.path} variant="primary" className="cc-welcome-cta">
              <FormattedMessage id="SIGN_IN"/>
            </Button>
            <Button as={Link} to={Routes.Signup.path} variant="outline-primary" className="cc-welcome-cta">
              <FormattedMessage id="SIGNUP_TITLE"/>
            </Button>
          </div>
          <Help/>
        </div>
      </Layout>
    );
  }

  // ── Écran définitif ────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className="text-center">
        <span className="cc-welcome-badge"><FontAwesomeIcon icon={faCheck}/></span>
        <h2 className="cc-welcome-title">
          <FormattedMessage id={upgrade ? 'WELCOME_UPGRADE_TITLE' : 'WELCOME_TITLE'}/>
        </h2>
        <p className="cc-welcome-lead">
          <FormattedMessage id={subtitleKey}
                            values={{plan: planKey ? <strong><FormattedMessage id={planKey}/></strong> : null}}/>
        </p>
      </div>

      {upgrade ? (
        <>
          <ul className="cc-welcome-features">
            {PAID_FEATURES.map(f => (
              <li key={f}><FontAwesomeIcon icon={faCheck}/> <FormattedMessage id={f}/></li>
            ))}
          </ul>
          <div className="text-center">
            <Button as={Link} to={Routes.DashboardOverview.path} variant="primary" className="cc-welcome-cta">
              <FormattedMessage id="WELCOME_GO_TO_DASHBOARD"/>
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="cc-welcome-mail">
            <FontAwesomeIcon icon={faEnvelope}/>
            <span>
              {status && status.maskedEmail
                ? <FormattedMessage id="WELCOME_CREDENTIALS" values={{email: <strong>{status.maskedEmail}</strong>}}/>
                : <FormattedMessage id="WELCOME_CREDENTIALS_NO_EMAIL"/>}
              {' '}
              <span className="text-muted"><FormattedMessage id="WELCOME_SPAM_HINT"/></span>
            </span>
          </div>

          <ol className="cc-welcome-steps">
            {ONBOARDING_STEPS.map((step, index) => (
              <li key={step}>
                <span className="cc-welcome-step-num">{index + 1}</span>
                <span>
                  <span className="cc-welcome-step-title"><FormattedMessage id={`WELCOME_STEP_${step}_TITLE`}/></span>
                  <span className="cc-welcome-step-body"><FormattedMessage id={`WELCOME_STEP_${step}_BODY`}/></span>
                </span>
              </li>
            ))}
          </ol>

          <div className="text-center">
            <Button as={Link} to={Routes.Signin.path} variant="primary" className="cc-welcome-cta">
              <FormattedMessage id="SIGN_IN"/>
            </Button>
          </div>
        </>
      )}

      <p className="cc-welcome-invoice"><FormattedMessage id="WELCOME_INVOICE_NOTE"/></p>
      <Help/>
    </Layout>
  );
};

const Layout = ({children}) => (
  <main>
    <section className="d-flex align-items-center my-5 mt-lg-5 mb-lg-5">
      <Container>
        <BrandLockup/>
        <Row className="justify-content-center">
          <Col xs={12} className="d-flex align-items-center justify-content-center">
            <div className="bg-white shadow-soft border rounded border-light p-4 p-lg-5 w-100 cc-welcome-card">
              {children}
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  </main>
);

const Help = () => (
  <p className="cc-welcome-help">
    <FormattedMessage id="WELCOME_HELP"
                      values={{mail: <a href="mailto:support@clickchauffeur.com">support@clickchauffeur.com</a>}}/>
  </p>
);
